import React, { useEffect, useMemo, useState } from 'react';
import {
  CLUB_PLAN_MONTHLY_PRICING_BY_TIER,
  getCompactFeatureStringsForClubTier,
  getRegionFromCountryCode,
  normalizeClubPlanTier,
  resolveSubscriptionPlanPrice,
  subscriptionYearlyAmountMajor,
  type ClubPlanRecommendationResult,
  type ClubPlanTier,
  type Plan,
} from '@court-sync/shared';
import { Check, CheckCircle, Loader2, Star, Zap } from 'lucide-react';
import { Card } from '@/components/ui';
import { subscriptionService } from '@/services/subscription';
import regionService from '@/services/regionService';
import {
  formatCurrency,
  getCurrencyFromRegion,
  getCurrencySymbol,
  type Currency,
} from '@/utils/currency';
import {
  persistBillingInterval,
  readStoredBillingInterval,
  resolveInitialBillingInterval,
} from '@/utils/billingInterval';

function getClubPlanIcon(tier: ClubPlanTier) {
  return tier === 'professional' ? <Zap className="h-6 w-6" /> : <Star className="h-6 w-6" />;
}

export interface ClubOnboardingPlanSelectionProps {
  recommendation: ClubPlanRecommendationResult;
  selectedClubPlanTier: ClubPlanTier;
  onSelectClubPlanTier?: (tier: ClubPlanTier) => void;
  marketCountry?: string | null;
}

export function ClubOnboardingPlanSelection({
  recommendation,
  selectedClubPlanTier,
  onSelectClubPlanTier,
  marketCountry,
}: ClubOnboardingPlanSelectionProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(() =>
    resolveInitialBillingInterval(null, readStoredBillingInterval())
  );
  const [currency, setCurrency] = useState<Currency>('AUD');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const region =
          marketCountry?.trim()
            ? getRegionFromCountryCode(marketCountry.trim())
            : await regionService.detectRegion();
        setCurrency(getCurrencyFromRegion(region));
        const available = await subscriptionService.getClubPlans(region);
        if (!cancelled) setPlans(available);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [marketCountry]);

  const plansByTier = useMemo(() => {
    const map = new Map<ClubPlanTier, Plan>();
    for (const plan of plans) {
      const tier = normalizeClubPlanTier(plan.internalId ?? null);
      if (tier) map.set(tier, plan);
    }
    return map;
  }, [plans]);

  const handleSelect = (tier: ClubPlanTier) => {
    onSelectClubPlanTier?.(tier);
  };

  const handleBillingChange = (interval: 'month' | 'year') => {
    setBillingInterval(interval);
    persistBillingInterval(interval);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const tiers: ClubPlanTier[] = ['starter', 'professional'];

  const getListPrice = (tier: ClubPlanTier, interval: 'month' | 'year'): number => {
    const monthly =
      CLUB_PLAN_MONTHLY_PRICING_BY_TIER[tier][currency as keyof typeof CLUB_PLAN_MONTHLY_PRICING_BY_TIER.starter] ??
      CLUB_PLAN_MONTHLY_PRICING_BY_TIER[tier].USD;
    return interval === 'year' ? subscriptionYearlyAmountMajor(monthly) : monthly;
  };

  const getPlanPrice = (
    plan: Plan | undefined,
    tier: ClubPlanTier,
    interval: 'month' | 'year' = billingInterval,
  ): number => {
    if (plan) {
      const resolved = resolveSubscriptionPlanPrice(plan, { currency, interval });
      if (resolved) return resolved.amountMajor;
    }
    return getListPrice(tier, interval);
  };

  const formatPlanPrice = (
    plan: Plan | undefined,
    tier: ClubPlanTier,
    interval: 'month' | 'year' = billingInterval,
  ): string => formatCurrency(getPlanPrice(plan, tier, interval), currency);

  const formatRoundedMonthlyFromYearly = (plan: Plan | undefined, tier: ClubPlanTier): string => {
    const yearlyPrice = getPlanPrice(plan, tier, 'year');
    return `${getCurrencySymbol(currency)}${Math.round(yearlyPrice / 12)}`;
  };

  return (
    <div className="space-y-6">
      {recommendation.reasonLabels.length > 0 ? (
        <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-4 dark:border-primary-700/60 dark:bg-primary-500/10">
          <p className="text-sm font-medium text-primary-800 dark:text-primary-200">
            Recommended: {recommendation.recommendedTier === 'professional' ? 'Club Pro' : 'Club'}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
            {recommendation.reasonLabels.map((label) => (
              <li key={label}>• {label}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-center sm:justify-start">
        <div
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50/90 p-1 shadow-sm dark:border-slate-600 dark:bg-slate-900/50"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            onClick={() => handleBillingChange('month')}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'month'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => handleBillingChange('year')}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              billingInterval === 'year'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            }`}
          >
            Yearly
            <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {tiers.map((tier) => {
          const plan = plansByTier.get(tier);
          const selected = selectedClubPlanTier === tier;

          return (
            <Card
              key={tier}
              className={`cursor-pointer border-2 p-5 transition-colors ${
                selected
                  ? 'border-primary-500 bg-primary-50/80 shadow-sm ring-2 ring-primary-500/20 ring-offset-2 ring-offset-background dark:border-primary-400 dark:bg-primary-500/10 dark:shadow-none dark:ring-primary-400/30'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600 dark:hover:bg-slate-900/70'
              }`}
              onClick={() => handleSelect(tier)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  {getClubPlanIcon(tier)}
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {tier === 'professional' ? 'Club Pro' : 'Club'}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {tier === 'professional'
                        ? 'Everything in Club plus a branded club subdomain'
                        : 'Court hire, memberships, match play, and facility operations'}
                    </p>
                  </div>
                </div>
                {selected ? (
                  <Check className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                ) : null}
              </div>
              <ul className="mt-4 space-y-1.5 text-left">
                {getCompactFeatureStringsForClubTier(tier).map((feature) => (
                  <li key={feature} className="flex gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <CheckCircle className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                {billingInterval === 'month' ? (
                  <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                    {formatPlanPrice(plan, tier, 'month')}
                    <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/mo</span>
                  </p>
                ) : (
                  <>
                    <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                      {formatRoundedMonthlyFromYearly(plan, tier)}
                      <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/mo</span>
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Billed annually</p>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
